import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import styles from '../../../styles/LoginRegister.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import { SUBSCRIPTION_ENDPOINTS } from '../../../constants/api';
import { useEnhancedAuth } from '../../../contexts/EnhancedAuthContext';
import LoadingSpinner from 'components/common/LoadingSpinner';

type PriceResponse = { base_price: number; ppu: number };
type PayfastPaymentData = { payfastUrl: string; params: Record<string, string> };

const quickOptions = [1, 5, 10, 20, 50];

const ChoosePlanStep: React.FC = () => {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('token');
  const { userDetails, userReady, checkPaymentStatus } = useEnhancedAuth();
  const hasCheckedRef = useRef(false);

  const [prices, setPrices] = useState<PriceResponse | null>(null);
  const [loadingPrices, setLoadingPrices] = useState(true);
  const [pricesError, setPricesError] = useState<string | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(true);

  const [numberOfSeats, setNumberOfSeats] = useState(1);
  const [action, setAction] = useState<'idle' | 'trial' | 'payment' | 'redirecting' | 'failed'>('idle');
  const [actionError, setActionError] = useState('');

  const actionInFlightRef = useRef(false);
  const [payfastData, setPayfastData] = useState<PayfastPaymentData | null>(null);
  const payfastFormRef = useRef<HTMLFormElement>(null);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  // No session at all — nothing to do here, send them to log in normally.
  useEffect(() => {
    if (!token) navigate('/LoginRegister');
  }, [token, navigate]);

  useEffect(() => {
    if (!token) return;

    if (!userReady) {
      return;
    }

    if (!userDetails?.agency) {
      navigate('/LoginRegister');
      return;
    }

    if (hasCheckedRef.current) return;
    hasCheckedRef.current = true;

    checkPaymentStatus().then((freshAgency) => {
      const status = (freshAgency ?? userDetails.agency)?.paymentStatus;
      if (status && status !== 'pending') {
        navigate('/dashboard');
        return;
      }
      setCheckingStatus(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userDetails, userReady]);

  useEffect(() => {
    const loadPrices = async () => {
      try {
        const res = await axios.get(SUBSCRIPTION_ENDPOINTS.PRICES);
        setPrices(res.data);
      } catch (err) {
        setPricesError('Unable to retrieve pricing information.');
      } finally {
        setLoadingPrices(false);
      }
    };
    loadPrices();
  }, []);

  const calculatePrice = (seats: number): number => (prices ? prices.base_price + (seats - 1) * prices.ppu : 0);

  const handleSeatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const seats = parseInt(e.target.value) || 1;
    setNumberOfSeats(Math.max(1, Math.min(50, seats)));
  };

  const handleStartTrial = async () => {
    if (actionInFlightRef.current) return;
    actionInFlightRef.current = true;
    setAction('trial');
    setActionError('');

    try {
      await axios.post(SUBSCRIPTION_ENDPOINTS.START_TRIAL, { numberOfSeats }, authHeaders);
      setTimeout(() => navigate('/dashboard'), 500);
    } catch (err: any) {
      setAction('failed');
      setActionError(err.response?.data?.message || 'Could not start your trial. Please try again.');
      actionInFlightRef.current = false;
    }
  };

  const handlePayWithPayfast = async () => {
    if (actionInFlightRef.current) return;
    actionInFlightRef.current = true;
    setAction('payment');
    setActionError('');

    try {
      const res = await axios.post(SUBSCRIPTION_ENDPOINTS.CREATE_PAYMENT, { numberOfSeats }, authHeaders);
      setPayfastData(res.data);
      setAction('redirecting');
    } catch (err: any) {
      setAction('failed');
      setActionError(err.response?.data?.message || 'Could not start the PayFast payment. Please try again.');
      actionInFlightRef.current = false;
    }
  };

  const handleCancel = () => {
    sessionStorage.clear();
  };

  useEffect(() => {
    if (payfastData && payfastFormRef.current) {
      payfastFormRef.current.submit();
    }
  }, [payfastData]);

  const handleRetry = () => {
    setAction('idle');
    setActionError('');
    actionInFlightRef.current = false;
    setPayfastData(null);
  };

  if (!token) return null; // redirecting via effect above

  if (checkingStatus || loadingPrices) return <LoadingSpinner />;

  if (pricesError || !prices) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <h2>Unable to load pricing</h2>
          <p>We couldn't retrieve the current package pricing. Please try again in a few moments.</p>
        </div>
      </div>
    );
  }

  const totalPrice = calculatePrice(numberOfSeats);
  const isBusy = action === 'trial' || action === 'payment';

  let content: React.ReactNode;

  if (action === 'redirecting') {
    content = (
      <div className={styles.formSection}>
        <div className={styles.paymentContainer}>
          <div className={`${styles.spinner} ${styles.processing}`}></div>
          <h3 className={styles.paymentTitle}>Redirecting to PayFast...</h3>
          <p className={styles.paymentDescription}>
            Please wait while we securely redirect you to PayFast to complete your payment.
          </p>
        </div>
        {payfastData && (
          <form ref={payfastFormRef} action={payfastData.payfastUrl} method="post" style={{ display: 'none' }}>
            {Object.entries(payfastData.params).map(([key, value]) => (
              <input key={key} type="hidden" name={key} value={value} />
            ))}
          </form>
        )}
      </div>
    );
  } else if (action === 'trial') {
    content = (
      <div className={styles.formSection}>
        <div className={styles.paymentContainer}>
          <div className={styles.paymentIcon}>🎉</div>
          <h3 className={`${styles.paymentTitle} ${styles.successTitle}`}>Welcome to RealtyGenie!</h3>
          <p className={styles.paymentDescription}>Your 7-day free trial has started. Enjoy full access to all features!</p>
          <div className={`${styles.spinner} ${styles.success}`}></div>
        </div>
      </div>
    );
  } else if (action === 'failed') {
    content = (
      <div className={styles.formSection}>
        <div className={styles.paymentContainer}>
          <div className={styles.paymentIcon}>❌</div>
          <h3 className={`${styles.paymentTitle} ${styles.errorTitle}`}>Something Went Wrong</h3>
          <div className={styles.errorMessage}>{actionError}</div>
          <div className={styles.buttonGroup}>
            <button type="button" onClick={handleRetry} className={`${buttonStyles.button} ${buttonStyles.formPrimary}`}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  } else {
    content = (
      <div className={styles.formSection}>
        <h2>Choose Your Plan</h2>
        <p className={styles.formDescription}>
          Your email is verified. Select the number of seats for your agency, then start a free trial or subscribe.
        </p>

        <div className={styles.formSectionSpacing}>
          <h3 className={styles.sectionHeader}>Seat Configuration</h3>

          <div className={styles.fieldContainer}>
            <label className={styles.fieldLabel}>Number of Seats (1-50) *</label>
            <input
              type="number"
              min="1"
              max="50"
              value={numberOfSeats}
              onChange={handleSeatChange}
              className={styles.fieldInput}
              required
            />
          </div>

          <div className={styles.quickSelectionSection}>
            <p className={styles.quickSelectionLabel}>Quick Selection:</p>
            <div className={styles.quickSelectionButtons}>
              {quickOptions.map(seats => (
                <button
                  key={seats}
                  type="button"
                  onClick={() => setNumberOfSeats(seats)}
                  className={`${styles.seatChip} ${numberOfSeats === seats ? styles.seatChipActive : ''}`}
                >
                  {seats} seat{seats > 1 ? 's' : ''}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.pricingContainer}>
            <h3 className={styles.pricingTitle}>Package Pricing</h3>
            <div className={styles.pricingRow}>
              <span>Base Package (1 seat)</span>
              <span>R{prices.base_price.toFixed(2)}</span>
            </div>
            {numberOfSeats > 1 && (
              <div className={styles.pricingRow}>
                <span>Additional seats ({numberOfSeats - 1} × R{prices.ppu})</span>
                <span>R{((numberOfSeats - 1) * prices.ppu).toFixed(2)}</span>
              </div>
            )}
            <div className={styles.pricingRow}>
              <span>VAT (15%)</span>
              <span>R{(totalPrice * 0.15).toFixed(2)}</span>
            </div>
            <hr className={styles.pricingDivider} />
            <div className={styles.pricingTotal}>
              <span>Total Monthly Price</span>
              <span>R{(totalPrice * 1.15).toFixed(2)}</span>
            </div>
            <p className={styles.pricingDisclaimer}>*Prices are in South African Rand (ZAR) and include VAT</p>
          </div>
        </div>

        <div className={styles.buttonGroup}>
          <button
            type="button"
            onClick={handleStartTrial}
            className={`${buttonStyles.button} ${buttonStyles.planTrial}`}
            disabled={isBusy}
          >
            🚀 Start 7-Day Free Trial
          </button>
          <button
            type="button"
            onClick={handlePayWithPayfast}
            className={`${buttonStyles.button} ${buttonStyles.planPayfast}`}
            disabled={isBusy}
          >
            {action === 'payment' ? 'Preparing Payment...' : '💳 Pay Now with PayFast'}
          </button>
        </div>

        <div style={{ textAlign: 'center' }}>
          <button
            type="button"
            onClick={handleCancel}
            className={`${buttonStyles.button} ${buttonStyles.planCancel}`}
          >
            Cancel
          </button>
        </div>

        <div className={styles.trialNote}>
          <small>After 7 days of a free trial, you'll need to subscribe to continue using the platform.</small>
        </div>
      </div>
    );
  }

  return <div className={styles.container}>{content}</div>;
};

export default ChoosePlanStep;